/**
 * Organization Configuration
 *
 * Handles extracting the organization name from:
 * - Subdomain in production (e.g., urbint-east.stormimpact.com)
 * - Environment variable in local development
 */

import envConfig from "./env";

const ORG_OVERRIDE_KEY = "stormimpact_local_org";

/**
 * Whether the ``?org=`` query / localStorage tenant override may be honored.
 *
 * Overrides are a dev/test convenience only. In production the tenant MUST be
 * derived from the (authoritative) host: honoring an attacker-supplied
 * ``?org=`` in a crafted login link would push the user into a spoofed tenant
 * context during SSO startup (cross-tenant auth confusion / phishing).
 */
function overridesAllowed(): boolean {
  const hostname = window.location.hostname;
  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
  const isDevDomain = hostname.startsWith("stormimpact-dev.");
  return isLocalhost || isDevDomain || envConfig.isDev;
}

/**
 * Get the organization name based on environment
 *
 * In development: Uses VITE_LOCAL_ORG_NAME (default: 'urbint-east')
 * In production: Extracts from subdomain (e.g., 'urbint-east' from 'urbint-east.stormimpact.com')
 */
export function getOrganizationName(): string {
  // Tenant overrides (query param + persisted localStorage) are honored only
  // in dev/local contexts. In production the host is authoritative.
  if (overridesAllowed()) {
    const orgParam = new URLSearchParams(window.location.search).get("org");
    if (orgParam) {
      localStorage.setItem(ORG_OVERRIDE_KEY, orgParam);
      return orgParam;
    }

    const storedOrg = localStorage.getItem(ORG_OVERRIDE_KEY);
    if (storedOrg) {
      return storedOrg;
    }
  }

  const hostname = window.location.hostname;

  // Handle localhost (fallback to local org name)
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "stormimpact-dev";
  }

  // In local development, use the configured value
  if (envConfig.isDev) {
    return envConfig.localOrgName;
  }

  // In production/staging, extract from subdomain

  // Extract subdomain (first part before the first dot)
  // e.g., 'urbint-east.stormimpact.com' -> 'urbint-east'
  const parts = hostname.split(".");

  // If we have a subdomain (more than 2 parts for domain.com, or more than 3 for domain.co.uk)
  if (parts.length >= 2) {
    const subdomain = parts[0];

    // Skip common non-org subdomains
    if (subdomain !== "www" && subdomain !== "app" && subdomain !== "api") {
      return subdomain;
    }
  }

  // Fallback to local org name if we can't determine
  console.warn(
    "[Organization] Could not determine org from hostname, using fallback:",
    envConfig.localOrgName,
  );
  return envConfig.localOrgName;
}

/**
 * Cached organization name (computed once on load)
 */
let cachedOrgName: string | null = null;

/**
 * Get organization name (cached)
 */
export function getOrgName(): string {
  if (!cachedOrgName) {
    cachedOrgName = getOrganizationName();
    console.log("[Organization] Using org-name:", cachedOrgName);
  }
  return cachedOrgName;
}

/**
 * Clear the cached org name (useful for testing)
 */
export function clearOrgNameCache(): void {
  cachedOrgName = null;
}

/**
 * Reset organization override and reload
 */
export function resetOrganizationOverride(): void {
  localStorage.removeItem(ORG_OVERRIDE_KEY);
  localStorage.removeItem("api_token");
  // Redirect to login without org param
  window.location.href = "/login";
}

export default getOrgName;
